import React, { useState, useMemo, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import {
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as TableRowMui,
  LinearProgress,
  Link,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Tooltip,
  Chip,
} from '@mui/material';
import styled from 'styled-components';
import { useAllNotificationDeliveries } from 'shared/apollo';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SearchIcon from '@mui/icons-material/Search';
import QuickView from './QuickView';
import _ from 'lodash';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import MultiFilter from 'shared/account/pages/Notifications/MultiFilter';
import MessagePreviewPopup from './MessagePreviewPopup';
import Alert from '@mui/lab/Alert';
import EditSvg from 'shared/statics/svg/Edit.svg';
import { formattedDateTime } from 'shared/utils/date';

const useStyles = makeStyles(theme => ({
  root: {
    margin: 'auto',
  },
  link: {
    color: '#0091ae',
    paddingLeft: 5,
    cursor: 'pointer',
  },
  container: {
    backgroundColor: '#f9f9f9f9',
    paddingLeft: '0px',
    paddingRight: '0px',
  },
  empty: {
    textAlign: 'center',
  },
}));

const CellWithTooltip = ({ children }) => (
  <Tooltip title={children}>
    <EllipsisText>{children}</EllipsisText>
  </Tooltip>
);

const EllipsisText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function Row(props) {
  const classes = useStyles();
  const {
    row,
    history,
    handleViewInvestment,
    handleOpenMessagePreview,
  } = props;
  const viewRule = () => {
    history.push(`/account/notifications?rule=${row.ruleName}&id=${row.rule}`);
  };
  let status = (
    <React.Fragment>
      <Status color="#E4002B"></Status>
      <span>Bounced</span>
    </React.Fragment>
  );
  if (row.deliveryStatus === 'SENT') {
    status = (
      <React.Fragment>
        <Status color="#99bd99"></Status>
        <span>Sent</span>
      </React.Fragment>
    );
  }
  if (row.deliveryStatus === 'DELIVERY') {
    status = (
      <React.Fragment>
        <Status color="#62C660"></Status>
        <span>Delivered</span>
      </React.Fragment>
    );
  }
  if (row.deliveryStatus === 'OPEN') {
    status = (
      <React.Fragment>
        <Status color="#62C660"></Status>
        <span>Opened</span>
      </React.Fragment>
    );
  }
  if (row.deliveryStatus === 'SCHEDULED') {
    status = (
      <React.Fragment>
        <Status color="#99bd99"></Status>
        <span>Scheduled</span>
      </React.Fragment>
    );
  }
  if (row.deliveryStatus === 'FAILED') {
    status = (
      <React.Fragment>
        <Status color="#E4002B"></Status>
        <span>Failed</span>
      </React.Fragment>
    );
  }
  if (row.deliveryStatus === 'COMPLAINT') {
    status = (
      <React.Fragment>
        <Status color="#E4002B"></Status>
        <span>Complaint</span>
      </React.Fragment>
    );
  }
  const viewInvestment = () => {
    handleViewInvestment(row.investment);
  };
  const viewMessagePreview = () => {
    if (row.deliveryStatus === 'SENT' && row.dbId) {
      handleOpenMessagePreview(row.dbId);
    }
  };
  return (
    <React.Fragment>
      <TableRow>
        <TableCell align="left">
          <CellWithTooltip>{formattedDateTime(row.date)}</CellWithTooltip>
        </TableCell>
        <TableCell align="left">{status}</TableCell>
        <TableCell align="left">
          <Tooltip title={row.ruleName} arrow interactive>
            <Link className="link" onClick={viewRule}>
              {row.ruleName}
            </Link>
          </Tooltip>
        </TableCell>
        <TableCell align="left">
          <Tooltip
            title={row.recipientEmail}
            placement="bottom-end"
            arrow
            interactive
          >
            <span>{row.recipientName}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="left">
          <CellWithTooltip>{row.portfolioName}</CellWithTooltip>
        </TableCell>
        <TableCell align="left">
          <CellWithTooltip>{row.npo?.name ?? '-'}</CellWithTooltip>
        </TableCell>
        <TableCell align="left">
          <Tooltip title={row.program} arrow interactive>
            <span>
              {row.program ? (
                <Link className={classes.link} onClick={viewInvestment}>
                  {row.program?.name}
                </Link>
              ) : (
                '-'
              )}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <MailOutlineIcon
            className={`link ${
              row.deliveryStatus !== 'SENT' ? 'disabled' : ''
            }`}
            onClick={viewMessagePreview}
          />
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const getFilterStr = values => {
  if (values[0] === 'All') return 'All';
  return values.filter(t => t !== 'All').join(', ');
};

const FilterGroup = ({
  history,
  npos,
  programs,
  search,
  setSearch,
  portfolios,
  setFilterPortfolio,
  recipients,
  setFilterRecipient,
  filterPortfolio,
  filterRecipient,
  setFilterNpo,
  filterNpo,
  setFilterProgram,
  filterProgram,
  filterStatus,
  setFilterStatus,
  availableStatus,
  filterRule,
  setFilterRule,
  availableRules,
  totalRow,
}) => {
  const clearAll = event => {
    event.preventDefault();
    setFilterPortfolio();
    setFilterRecipient();
    setFilterStatus();
    setFilterRule();
    setFilterNpo();
    setFilterProgram();
    setSearch('');
  };
  const viewRules = () => {
    history.push('/account/notifications');
  };
  return (
    <Heading>
      <RowH>
        <ColH>
          <h2>
            Notification Logs <label>({totalRow})</label>
          </h2>
        </ColH>
        <TextField
          placeholder="Search notifications"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button onClick={viewRules}>Notification Rules</Button>
      </RowH>
      <RowH>
        <ColH>
          <span>Filter By:</span>
        </ColH>
        <LinkClear onClick={clearAll}>Clear filters</LinkClear>
      </RowH>
      <RowH>
        <ColH className="filters">
          <MultiFilter
            options={availableStatus}
            onChange={setFilterStatus}
            value={filterStatus}
            placeholder="Status"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={availableRules}
            onChange={setFilterRule}
            value={filterRule}
            placeholder="Trigger (Rule)"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={recipients}
            onChange={setFilterRecipient}
            value={filterRecipient}
            placeholder="Recipient(s)"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={portfolios}
            onChange={setFilterPortfolio}
            value={filterPortfolio}
            placeholder="Portfolio"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={npos}
            onChange={setFilterNpo}
            value={filterNpo}
            placeholder="NPO"
          />
        </ColH>
        <ColH className="filters">
          <MultiFilter
            options={programs}
            onChange={setFilterProgram}
            value={filterProgram}
            placeholder="Program"
          />
        </ColH>
      </RowH>
      <RowH>
        {filterStatus && filterStatus.length > 0 && (
          <Chip
            label={`Status: ${getFilterStr(filterStatus)}`}
            onDelete={() => setFilterStatus()}
          />
        )}
        {filterRule && filterRule.length > 0 && (
          <Chip
            label={`Trigger: ${getFilterStr(filterRule)}`}
            onDelete={() => setFilterRule()}
          />
        )}
        {filterRecipient && filterRecipient.length > 0 && (
          <Chip
            label={`Recipients: ${getFilterStr(filterRecipient)}`}
            onDelete={() => setFilterRecipient()}
          />
        )}
        {filterPortfolio && filterPortfolio.length > 0 && (
          <Chip
            label={`Portfolio: ${getFilterStr(filterPortfolio)}`}
            onDelete={() => setFilterPortfolio()}
          />
        )}
        {filterNpo && filterNpo.length > 0 && (
          <Chip
            label={`Portfolio: ${getFilterStr(filterNpo)}`}
            onDelete={() => setFilterNpo()}
          />
        )}
        {filterProgram && filterProgram.length > 0 && (
          <Chip
            label={`Portfolio: ${getFilterStr(filterProgram)}`}
            onDelete={() => setFilterProgram()}
          />
        )}
      </RowH>
    </Heading>
  );
};

export default function Deliveries({ history }) {
  const classes = useStyles();
  const [offset, setOffset] = useState(0);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const [rows, setRows] = useState([]);
  const { isLoading, refresh } = useAllNotificationDeliveries(
    20,
    offset,
    setIsEndOfList,
  );
  const [search, setSearch] = useState('');
  const [filterPortfolio, setFilterPortfolio] = useState();
  const [filterRecipient, setFilterRecipient] = useState();
  const [filterNpo, setFilterNpo] = useState();
  const [filterProgram, setFilterProgram] = useState();
  const [filterStatus, setFilterStatus] = useState();
  const [filterRule, setFilterRule] = useState();
  const [selectedInvestment, setSelectedInvestment] = useState();
  const [popupIsOpen, setPopupIsOpen] = useState(false);
  const [previewId, setPreviewId] = useState();

  useEffect(() => {
    const handleScroll = () => {
      const threshold = 5;
      const currentPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const pageHeight = document.documentElement.scrollHeight;
      if (
        pageHeight - currentPosition <= threshold &&
        !isEndOfList &&
        !isLoading
      ) {
        setOffset(prevOffset => prevOffset + 20);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isEndOfList, isLoading]);

  const fetchData = async () => {
    const newData = await refresh();
    const newRows = (newData?.data?.allNotificationDeliveries?.edges || []).map(
      row => row.node,
    );
    if (newRows) {
      console.log('adding new data...');
      setRows(prevData => [...prevData, ...newRows]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [offset, refresh]);

  const [sortConf, setSortConf] = useState({
    activeField: 'date',
    date: 'asc',
    status: 'asc',
    rule: 'asc',
    recipient: 'asc',
    portfolio: 'asc',
    npo: 'asc',
    program: 'asc',
  });

  const availablePortfolios = _.uniqBy(rows.map(r => r.portfolioName));
  availablePortfolios.unshift('All');
  const availableRecipients = _.uniqBy(rows.map(r => r.recipientName));
  availableRecipients.unshift('All');
  const availableNpos = _.uniqBy(
    rows.filter(r => r.npo?.name).map(r => r.npo?.name),
  );
  availableNpos.unshift('All');
  const availablePrograms = _.uniqBy(
    rows.filter(r => r.program).map(r => r.program?.name),
  );
  availablePrograms.unshift('All');
  const availableStatus = [
    'All',
    'Sent',
    'Scheduled',
    'Failed',
    'Delivered',
    'Open',
    'Bounced',
  ];
  const availableRules = _.uniqBy(rows.map(r => r.ruleName));
  availableRules.unshift('All');

  console.log(availableRules);

  const filteredRows = useMemo(() => {
    let data = search
      ? rows.filter(row => {
          return (
            row.ruleName.toUpperCase().indexOf(search.toUpperCase()) > -1 ||
            row.recipientName.toUpperCase().indexOf(search.toUpperCase()) >
              -1 ||
            row.portfolioName.toUpperCase().indexOf(search.toUpperCase()) > -1
          );
        })
      : rows;
    /* FILTERS */
    if (filterStatus && filterStatus.length > 0) {
      const GET_FILTER_NAME = {
        SENT: 'Sent',
        SCHEDULED: 'Scheduled',
        FAILED: 'Failed',
        DELIVERY: 'Delivered',
        OPEN: 'Opened',
        BOUNCE: 'Bounced',
      };
      data = data.filter(
        item =>
          filterStatus.indexOf(
            GET_FILTER_NAME?.[item.deliveryStatus] ?? 'Bounced',
          ) > -1,
      );
    }
    if (filterRule && filterRule.length > 0) {
      data = data.filter(item => filterRule.indexOf(item.ruleName) > -1);
    }
    if (filterRecipient && filterRecipient.length > 0) {
      data = data.filter(
        item => filterRecipient.indexOf(item.recipientName) > -1,
      );
    }
    if (filterPortfolio && filterPortfolio.length > 0) {
      data = data.filter(
        item => filterPortfolio.indexOf(item.portfolioName) > -1,
      );
    }
    if (filterNpo && filterNpo.length > 0) {
      data = data.filter(item => filterNpo.indexOf(item.npo?.name) > -1);
    }
    if (filterProgram && filterProgram.length > 0) {
      data = data.filter(item => filterProgram.indexOf(item.program) > -1);
    }
    /* SORTING */
    if (sortConf.activeField === 'date') {
      return _.orderBy(data, row => formattedDateTime(row.date), sortConf.date);
    }
    if (sortConf.activeField === 'status') {
      return _.orderBy(data, row => row.deliveryStatus, sortConf.status);
    }
    if (sortConf.activeField === 'rule') {
      return _.orderBy(data, row => row.ruleName, sortConf.rule);
    }
    if (sortConf.activeField === 'recipient') {
      return _.orderBy(data, row => row.recipientName, sortConf.recipient);
    }
    if (sortConf.activeField === 'portfolio') {
      return _.orderBy(data, row => row.portfolioName, sortConf.portfolio);
    }
    if (sortConf.activeField === 'npo') {
      return _.orderBy(data, row => row.npo?.name, sortConf.npo);
    }
    if (sortConf.activeField === 'program') {
      return _.orderBy(data, row => row.program, sortConf.program);
    }
    return data;
  }, [
    sortConf,
    rows,
    search,
    filterPortfolio,
    filterRecipient,
    filterNpo,
    filterProgram,
    filterStatus,
    filterRule,
  ]);

  const handleViewInvestment = investmentId => {
    setSelectedInvestment(investmentId);
  };

  const onPressAr = activeField => {
    const copyConf = { ...sortConf, activeField };
    const ascDesc = copyConf[activeField] === 'asc' ? 'desc' : 'asc';
    copyConf[activeField] = ascDesc;
    setSortConf(copyConf);
  };

  const handleOpenMessagePreview = deliveryId => {
    setPreviewId(deliveryId);
    setPopupIsOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewId();
    setPopupIsOpen(false);
  };

  const sortArrowDate =
    sortConf.date === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowStatus =
    sortConf.status === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowRule =
    sortConf.rule === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowRecipient =
    sortConf.recipient === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowPortfolio =
    sortConf.portfolio === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowNpo =
    sortConf.npo === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;
  const sortArrowProgram =
    sortConf.program === 'asc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />;

  return (
    <Container maxWidth={false} className={classes.container}>
      <Grid
        container
        spacing={2}
        justify="center"
        alignItems="center"
        className={classes.root}
      >
        <FilterGroup
          history={history}
          npos={availableNpos}
          programs={availablePrograms}
          portfolios={availablePortfolios}
          setFilterPortfolio={setFilterPortfolio}
          recipients={availableRecipients}
          setFilterRecipient={setFilterRecipient}
          setFilterNpo={setFilterNpo}
          setFilterProgram={setFilterProgram}
          search={search}
          setSearch={setSearch}
          filterPortfolio={filterPortfolio}
          filterRecipient={filterRecipient}
          filterNpo={filterNpo}
          filterProgram={filterProgram}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          availableStatus={availableStatus}
          filterRule={filterRule}
          setFilterRule={setFilterRule}
          availableRules={availableRules}
          totalRow={filteredRows.length}
        />
        {isLoading && (
          <Grid item xs={12}>
            <LinearProgress variant="query" />
          </Grid>
        )}
        <Grid
          container
          spacing={2}
          justify="center"
          alignItems="center"
          className={classes.root}
        >
          {rows.length > 0 && (
            <Grid item xs={12}>
              <TableRoot>
                <Table aria-label="collapsible table">
                  <TableHead>
                    <TableRow>
                      <TableCell onClick={() => onPressAr('date')} align="left">
                        Date {sortArrowDate}
                      </TableCell>
                      <TableCell
                        onClick={() => onPressAr('status')}
                        align="left"
                      >
                        Status {sortArrowStatus}
                      </TableCell>
                      <TableCell onClick={() => onPressAr('rule')} align="left">
                        Rule {sortArrowRule}
                      </TableCell>
                      <TableCell
                        onClick={() => onPressAr('recipient')}
                        align="left"
                      >
                        Recipient {sortArrowRecipient}
                      </TableCell>
                      <TableCell
                        onClick={() => onPressAr('portfolio')}
                        align="left"
                      >
                        Portfolio {sortArrowPortfolio}
                      </TableCell>
                      <TableCell onClick={() => onPressAr('npo')} align="left">
                        NPO {sortArrowNpo}
                      </TableCell>
                      <TableCell
                        onClick={() => onPressAr('program')}
                        align="left"
                      >
                        Program {sortArrowProgram}
                      </TableCell>
                      <TableCell align="center">Message Sent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <>
                        <AlertSpacer />
                        <Alert severity="info">
                          No records found to match your search
                        </Alert>
                      </>
                    ) : (
                      filteredRows.map(row => (
                        <Row
                          key={row.dbId}
                          row={row}
                          history={history}
                          handleViewInvestment={handleViewInvestment}
                          handleOpenMessagePreview={handleOpenMessagePreview}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableRoot>
            </Grid>
          )}
          {!isLoading && rows.length === 0 && (
            <Grid item xs={12} className={classes.empty}>
              <Typography component="subtitle2" variant="subtitle2" noWrap>
                {'There are no notifications yet...'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      <QuickView
        investmentId={selectedInvestment}
        open={!!selectedInvestment}
        onClose={() => setSelectedInvestment()}
      />
      {previewId && popupIsOpen && (
        <MessagePreviewPopup
          id={previewId}
          open={popupIsOpen}
          onClose={handleClosePreview}
        />
      )}
    </Container>
  );
}

export const TableRoot = styled(TableContainer)`
  table {
    border-spacing: 0px 8px;
    border-collapse: separate;
    padding-left: 24px;
    padding-right: 24px;
  }
  .link.disabled {
    color: #ccc !important;
  }
  .MuiTableHead-root {
    tr {
      height: 16px !important;
      &:hover {
        background-color: transparent;
      }
      th {
        font-weight: 700;
        font-size: 16px;
        color: #48596b;
        border-right: solid 1px #cbd6e2;
        border-bottom: none;
        padding-top: 0px !important;
        padding-bottom: 0px !important;
        &:first-of-type {
          color: #0072a2;
        }
        svg {
          transform: translateY(7px);
        }
        &:nth-of-type(8) {
          border-right: none;
        }
      }
    }
  }
  .MuiTableBody-root {
    tr {
      background-color: #fff;
      &:hover {
        background-color: #eaf0f6;
        .MuiIconButton-label {
          opacity: 1;
        }
      }
      td {
        font-weight: 400;
        font-size: 16px;
        color: #48596b;
        border-bottom: solid 1px #cbd6e2;
        border-top: solid 1px #cbd6e2;
        padding-bottom: 16px;
        padding-top: 16px;
        max-width: 100px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        &:first-of-type {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
          border-left: solid 1px #cbd6e2;
        }
        &:last-of-type {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
          border-right: solid 1px #cbd6e2;
        }
        .MuiIconButton-label {
          opacity: 0;
          color: #0072a2;
        }
        .link {
          color: #0091ae;
          padding-left: 5px;
          cursor: pointer;
          &:hover {
            &:after {
              content: '';
              display: inline-block;
              margin-left: 5px;
              width: 17px;
              height: 16px;
              background-size: cover;
              background-image: url(${EditSvg});
            }
          }
        }
      }
    }
  }
`;

const TableRow = styled(TableRowMui)`
  cursor: pointer;
  &:hover {
    background-color: #0091ae1f;
  }
  .MuiTooltip-popper {
    background-color: #ffffff;
    height: 48px;
  }
`;

const Heading = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: #ebf0f6;
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 24px;
  div:first-child {
    position: relative;
  }
  span {
    font-size: 16px;
    font-weight: 700;
    color: #48596b;
  }
  h2 {
    font-size: 32px;
    font-weight: 400;
    color: #48596b;
    margin-top: -8px;
    span {
      color: #0072a2;
      cursor: pointer;
    }
    label {
      font-weight: 700;
      font-size: 24px;
      color: #778ba5;
    }
  }
  .MuiButtonGroup-root {
    width: 100%;
    background-color: white;
    border: solid 1px #cbd6e2;
    padding: 5px;
    max-height: 48px;
    button {
      max-height: 34px;
      justify-content: left;
      span {
        font-size: 16px;
        font-weight: 400;
        color: #535353;
      }
      &:first-of-type {
        width: 100%;
        text-transform: none;
      }
      border: none;
    }
  }
  .options {
    width: 100%;
    transform: translate(0px, 46px);
    z-index: 1;
  }
  .MuiFormControl-root {
    margin-top: -3px !important;
  }
`;

const RowH = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  margin-bottom: 17px;
  button {
    text-transform: none;
    height: 48px;
    span {
      color: #ffffff;
      font-size: 16px;
      font-weight: 700;
    }
  }
  .MuiFormControl-root {
    margin-top: 0px;
    margin-bottom: 0px;
    margin-right: 16px;
  }
  input {
    z-index: 1;
    color: #535353;
    font-size: 16px;
    font-weight: 400;
    height: 32px;
  }
  .MuiInputAdornment-root {
    z-index: 1;
  }
  fieldset {
    border: solid 1px #cbcbcb;
    background-color: #ffffff;
  }
  .MuiChip-root {
    background-color: #48596b;
    height: 32px;
    margin-right: 10px;
    max-width: 320px;
    span {
      color: #ffffff;
      font-size: 16px;
      font-weight: 400;
    }
    svg {
      color: #ffffff;
    }
  }
`;

const ColH = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  &.filters {
    padding-right: 16px;
    max-width: 25%;
    position: relative;
  }
`;

const LinkClear = styled.span`
  font-size: 16px !important;
  font-weight: 400 !important;
  color: #0072a2 !important;
  text-decoration: underline;
  cursor: pointer;
`;

const Status = styled.div`
  display: inline-block;
  border-radius: 8px;
  transform: translateY(3px);
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  margin-right: 5px;
`;

const AlertSpacer = styled.div`
  height: 30px;
`;
